import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import {
	MatLegacyAutocomplete as MatAutocomplete,
	MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
} from '@angular/material/legacy-autocomplete';

import { Subject } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';

import {
	GET_CARRIER_BUSINESS_NAME,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';

@Component({
	selector: 'app-carrier-search-popover',
	templateUrl: './carrier-search-popover.component.html',
	styleUrls: ['./carrier-search-popover.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class CarrierSearchPopoverComponent implements OnInit {
	@Input() carrierSelected: any = null;
	@Input() placeholder: string = 'Carrier';
	@Input() customFormFieldClass: string = 'custom-form-field';
	@Input() showCloseIcon: boolean = false;
	@Input() usePopover: boolean = true;
	@Input() useAutoComplete: boolean = false;
	@Input() inNetwork: boolean = true;
	@Input() resetInputAfterSelection: boolean = false;
	@Input() disabled: boolean = false;
	@Input() useMultiSelect: boolean = false;
	@Input() selectedCarriersList: any = [];
	@Input() excludeAccountCarrierMaps: boolean = false;
	@Input() excludeInfractionDetails: boolean = false;

	@Output() setCarrier = new EventEmitter<any>();
	@Output() carrierRemoved = new EventEmitter<any>();

	GET_CARRIER_BUSINESS_NAME = GET_CARRIER_BUSINESS_NAME;
	carriersList: Array<any> = [];
	isLoadingCarriersList: boolean = false;
	searchText: string = '';
	filterSelectedCarrierslistText: string = '';

	debouncer: Subject<void> = new Subject<void>();
	@ViewChild(MtxPopoverTrigger) trigger: MtxPopoverTrigger;
	@ViewChild('carrierInput') carrierInput: ElementRef;
	@ViewChild('auto') matAutocomplete: MatAutocomplete;

	constructor(
		private urlService: UrlService,
		private restService: RestService
	) {}

	ngOnInit(): void {
		this.debouncer
			.pipe(
				debounceTime(300),
				tap(() => {
					this.isLoadingCarriersList = true;
					this.carriersList = [];
				}),
				switchMap(() => {
					const url = this.urlService.searchCarriers(0, 5, {
						excludeAccountCarrierMaps: this.excludeAccountCarrierMaps,
						excludeInfractionDetails: this.excludeInfractionDetails,
					});
					return this.restService.POST(url, {
						businessName: this.searchText,
						inNetwork: this.inNetwork,
						ignoreContactCount: true,
					});
				})
			)
			.subscribe((response: any) => {
				this.carriersList = response['content'].sort((a, b) => {
					const nameA = GET_CARRIER_BUSINESS_NAME(a.businessName, a.doingBusinessAs).toLowerCase();
					const nameB = GET_CARRIER_BUSINESS_NAME(b.businessName, b.doingBusinessAs).toLowerCase();
					return 0 - (nameA > nameB ? -1 : 1);
				});
				this.isLoadingCarriersList = false;
			});
	}

	ngOnChanges() {
		if (IS_NULL_OR_UNDEFINED(this.carrierSelected)) {
			this.searchText = '';
		} else {
			this.searchText = this.carrierSelected;
		}
	}

	inputChanged(searchedText: string) {
		if (IS_NOT_EMPTY(searchedText)) {
			this.debouncer.next();
		} else {
			this.setCarrier.emit(null);
			this.isLoadingCarriersList = false;
		}
	}

	setSelectedCarrier(carrier: any) {
		this.carrierSelected = carrier;
		this.updatePlaceholderAndClose();
	}

	updatePlaceholderAndClose() {
		this.placeholder = GET_CARRIER_BUSINESS_NAME(this.carrierSelected.businessName, this.carrierSelected.doingBusinessAs);
		this.setCarrier.emit(this.carrierSelected);
		if (IS_NOT_NULL_OR_UNDEFINED(this.trigger)) {
			this.trigger.closePopover();
		}
	}

	renderDownArrowIcon(): boolean {
		if (!this.showCloseIcon || (this.showCloseIcon && IS_NULL_OR_UNDEFINED(this.carrierSelected))) {
			return true;
		}
		return false;
	}

	renderCloseArrowIcon(): boolean {
		if (this.showCloseIcon && IS_NOT_NULL_OR_UNDEFINED(this.carrierSelected)) {
			return true;
		}
		return false;
	}

	removeSelectedCarrier() {
		this.carrierSelected = null;
		this.setCarrier.emit(null);
		this.placeholder = 'Carrier';
	}

	selected(event: MatAutocompleteSelectedEvent | any): void {
		this.setCarrier.emit(event.option.value);
		if (this.resetInputAfterSelection) {
			this.searchText = '';
			this.placeholder = 'Carrier';
		}
	}

	displayFn(carrier: any) {
		if (!carrier) return '';
		return GET_CARRIER_BUSINESS_NAME(carrier.businessName, carrier.doingBusinessAs);
	}

	removeCarrierSelection(carrier) {
		this.carrierRemoved.emit(carrier);
	}

	isCarrierSelected(carrier: any): boolean {
		const carrierFound = this.selectedCarriersList.find((c) => c.id === carrier.id);
		return IS_NOT_NULL_OR_UNDEFINED(carrierFound);
	}
}
